/**
 * Rendu markdown-light du body d'un message — SANITIZATION STRICTE :
 * TOUT le HTML est échappé D'ABORD, puis les transformations (code/gras/
 * italique/liens) sont appliquées sur le texte échappé. Le HTML produit est
 * intégralement construit ici (aucun HTML utilisateur ne passe), et les hrefs
 * sont limités à http(s) — jamais de javascript:.
 */

// Délimiteur de placeholder en zone privée Unicode — aucune collision possible
// avec du texte utilisateur réel.
const CODE_TOKEN = '\uE000';

const UUID_TOKEN = /@\[([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\]/g;

export function renderChatBody(
  body: string | null,
  mentionNames?: ReadonlyMap<string, string>,
  mentionFallback = 'Utilisateur',
): string {
  if (!body) {
    return '';
  }

  // Message composé d'un SEUL emoji → affiché en très grand (style
  // messenger, taille dans chat-view.component.scss). Le contenu reste
  // échappé comme tout le reste.
  if (isSingleEmoji(body)) {
    return `<span class="chat-emoji-only">${escapeHtml(body.trim())}</span>`;
  }

  let text = escapeHtml(body);

  // 0) Chips de mention (T7) — remplacées APRÈS échappement : le token
  //    @[uuid] survit intact à escapeHtml, et le NOM inséré est ré-échappé.
  text = text.replace(UUID_TOKEN, (_match, uuid: string) => {
    const name = mentionNames?.get(uuid.toLowerCase()) ?? mentionFallback;
    return `<span class="chat-mention">@${escapeHtml(name)}</span>`;
  });
  text = text.replace(/@\[all\]/g, '<span class="chat-mention chat-mention-all">@all</span>');

  // 1) Spans de code `...` — extraits AVANT le reste (pas de markdown dedans).
  const codeSpans: string[] = [];
  text = text.replace(/`([^`\n]+)`/g, (_match, code: string) => {
    codeSpans.push('<code>' + code + '</code>');
    return CODE_TOKEN + (codeSpans.length - 1) + CODE_TOKEN;
  });

  // 2) Liens auto-cliquables http(s) uniquement (le texte est déjà échappé;
  //    &amp; dans les query strings est du HTML d'attribut valide).
  text = text.replace(/https?:\/\/[^\s<>"']+/gi, (url: string) => {
    if (!/^https?:\/\//i.test(url)) {
      return url;
    }
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

  // 3) Gras **x** puis italique *x* (ordre important).
  text = text.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');

  // 4) Sauts de ligne.
  text = text.replace(/\n/g, '<br>');

  // 5) Restauration des spans de code.
  text = text.replace(new RegExp(CODE_TOKEN + '(\\d+)' + CODE_TOKEN, 'g'), (_match, index: string) => {
    return codeSpans[Number(index)] ?? '';
  });

  return text;
}

/**
 * Vrai si le body (trimé) est UN seul emoji : un unique graphème (les
 * séquences ZWJ, tons de peau, drapeaux comptent pour un) composé uniquement
 * de caractères emoji — et pas un simple chiffre/symbole (qui sont
 * Emoji_Component sans être pictographiques).
 */
export function isSingleEmoji(body: string): boolean {
  const text = body.trim();
  // Les séquences ZWJ les plus longues (famille 4 personnes) ≈ 11 code points.
  if (!text || text.length > 20) {
    return false;
  }

  // Un seul graphème perçu.
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const graphemes = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)];
    if (graphemes.length !== 1) {
      return false;
    }
  } else if ([...text].length > 11) {
    return false;
  }

  // Composé uniquement de matière emoji (pictogrammes, composants, ZWJ
  // \u200d, sélecteur de variante \ufe0f)…
  if (!/^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|\u200d|\ufe0f)+$/u.test(text)) {
    return false;
  }
  // …et contenant un vrai cœur d'emoji : pictogramme, drapeau (regional
  // indicators) ou keycap \u20e3 — exclut "1", "#", etc. (Emoji_Component seuls).
  return /[\p{Extended_Pictographic}\p{Regional_Indicator}\u20e3]/u.test(text);
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
